import React from 'react';
import { Text, Platform, StyleProp, TextStyle } from 'react-native';
import { openUrlInApp } from './sameOriginUrl';

/**
 * A single styled run of inline text produced by {@link parseInlineMarkdown}.
 * Flags stack (e.g. bold + italic); `link` turns the run into a tappable URL.
 */
export interface InlineToken {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
  /** When set, the run renders as a tappable link to this URL. */
  link?: string;
}

/**
 * Combined inline-markdown scanner. Alternatives are ordered longest-marker
 * first so `***` wins over `**` wins over `*` at the same position (regex
 * alternation tries branches left-to-right at a given index).
 *
 * Deliberately *asterisk-only* for emphasis: `_`/`__` are NOT treated as
 * italic/bold because underscores routinely appear inside identifiers,
 * file names and URLs (`__init__`, `some_var`, `a_b`) — GPT emits `*`/`**`
 * for emphasis far more often, so this avoids corrupting plain prose.
 *
 * Groups: 1 code · 2 bold-italic · 3 bold · 4 strike · 5 link-title ·
 * 6 link-url · 7 bare-url · 8 italic.
 */
const INLINE_RE = new RegExp(
  [
    '`([^`\\n]+?)`', // inline code
    '\\*\\*\\*([^\\n]+?)\\*\\*\\*', // bold italic
    '\\*\\*([^\\n]+?)\\*\\*', // bold
    '~~([^\\n]+?)~~', // strikethrough
    '\\[([^\\]]+?)\\]\\((https?:\\/\\/[^\\s)]+)\\)', // [title](url)
    '(https?:\\/\\/[^\\s]+)', // bare url
    '\\*([^*\\n]+?)\\*', // italic
  ].join('|'),
  'g',
);

/** Fenced code block: ```lang\n…\n``` — the language hint (if any) is dropped. */
const FENCE_RE = /```[^\n]*\n?([\s\S]*?)```/g;

/** Cheap gate: skip all parsing when the text has no markdown/URL at all. */
const HAS_MARKDOWN_RE = /[*`~#>[\]]|https?:\/\/|(^|\n)[ \t]*[-+]\s|(^|\n)[ \t]*\d+[.)]\s/;

const MONO = Platform.OS === 'ios' ? 'Menlo' : 'monospace';

const CODE_STYLE: TextStyle = {
  fontFamily: MONO,
  fontSize: 13,
  backgroundColor: 'rgba(127,127,127,0.16)',
  borderRadius: 4,
};

const HEADING_STYLE: TextStyle = { fontWeight: '700' };
const QUOTE_STYLE: TextStyle = { fontStyle: 'italic', opacity: 0.85 };

/** Split off trailing sentence punctuation so it isn't swallowed into a URL. */
function splitTrailingPunctuation(url: string): { url: string; tail: string } {
  const m = url.match(/^(.*?)([)».,!?;:]*)$/s);
  return m ? { url: m[1], tail: m[2] } : { url, tail: '' };
}

/**
 * Parse one line (no newlines) into styled inline runs. Pure and exported so
 * unit tests can pin the tokenisation without rendering React Native.
 */
export function parseInlineMarkdown(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  if (!text) return tokens;

  let last = 0;
  INLINE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = INLINE_RE.exec(text)) !== null) {
    const start = m.index;
    const pending: InlineToken[] = [];

    if (m[1] !== undefined) pending.push({ text: m[1], code: true });
    else if (m[2] !== undefined) pending.push({ text: m[2], bold: true, italic: true });
    else if (m[3] !== undefined) pending.push({ text: m[3], bold: true });
    else if (m[4] !== undefined) pending.push({ text: m[4], strike: true });
    else if (m[5] !== undefined && m[6] !== undefined) pending.push({ text: m[5], link: m[6] });
    else if (m[7] !== undefined) {
      const { url, tail } = splitTrailingPunctuation(m[7]);
      pending.push({ text: url, link: url });
      if (tail) pending.push({ text: tail });
    } else if (m[8] !== undefined) pending.push({ text: m[8], italic: true });

    if (pending.length === 0) {
      INLINE_RE.lastIndex = start + 1;
      continue;
    }

    if (start > last) tokens.push({ text: text.slice(last, start) });
    tokens.push(...pending);
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) tokens.push({ text: text.slice(last) });
  return tokens;
}

function tokenStyle(t: InlineToken): TextStyle | undefined {
  const style: TextStyle = {};
  if (t.bold) style.fontWeight = '700';
  if (t.italic) style.fontStyle = 'italic';
  if (t.strike) style.textDecorationLine = 'line-through';
  if (t.code) Object.assign(style, CODE_STYLE);
  return Object.keys(style).length ? style : undefined;
}

/**
 * CORE-1497 — почему у части ответов ИИ другой шрифт.
 *
 * Пузырь сообщения задаёт свой шрифт (`uiFont()` в `bubbleText`), а куски,
 * которые рождает разметка, — жирное, курсив, ссылки, заголовки, цитаты —
 * уезжали во вложенный `<Text>` только со стилем токена, без семейства. На вебе
 * у `Text` из react-native-web своё базовое `font-family`, и вложенный узел
 * берёт его, а не родительское: обычный текст оставался в шрифте платформы, а
 * размеченные куски — в системном. Внутри одного ответа получались два шрифта.
 *
 * Отсюда и «некоторые ответы»: без разметки сообщение возвращается голой
 * строкой и наследует шрифт правильно, а с разметкой — нет.
 *
 * `baseStyle` необязателен намеренно: личные чаты (`MessageBubble`) своего
 * шрифта не задают, и навязывать им шрифт Агапеллы эта задача не просит.
 */

/**
 * Render message body text as a safe subset of Markdown with tappable links.
 *
 * Supported (the formats a GPT model commonly emits): `**bold**`, `*italic*`,
 * `***bold italic***`, `~~strike~~`, `` `inline code` ``, ```` ```code blocks``` ````,
 * `#`…`######` headings, `-`/`+`/`*` and `1.` lists, `>` blockquotes and both
 * `[title](url)` and bare URLs. Everything else stays plain text.
 *
 * `onOpenLink` lets the caller decide what a tap does (native: open a room
 * invite in-app, else browser; web: open the URL). Without it we fall back to
 * {@link openUrlInApp}: on web an address of our own origin navigates in this
 * tab (CORE-1237 — `Linking.openURL` there is `window.open(_blank)`, i.e. a
 * second copy of the app, or nothing at all under a popup blocker); a foreign
 * link, and every link on native, still goes through `Linking.openURL`.
 *
 * Shared by the native thread (`MessageBubble`) and the web/community + AI
 * companion thread (`AgapellaChatThread`) so formatting stays identical.
 */
export function renderTextWithLinks(
  text: string,
  linkStyle: StyleProp<TextStyle>,
  onOpenLink?: (url: string) => void,
  baseStyle?: StyleProp<TextStyle>,
): React.ReactNode {
  if (!text || !HAS_MARKDOWN_RE.test(text)) return text;

  const open = (url: string) => (onOpenLink ? onOpenLink(url) : void openUrlInApp(url));
  const out: React.ReactNode[] = [];
  const counter = { n: 0 };

  const pushTokens = (line: string, wrapStyle?: TextStyle) => {
    const tokens = parseInlineMarkdown(line);
    const nodes: React.ReactNode[] = tokens.map((t) => {
      if (t.link) {
        return (
          <Text key={`t${counter.n++}`} style={[baseStyle, linkStyle]} onPress={() => open(t.link!)}>
            {t.text}
          </Text>
        );
      }
      const style = tokenStyle(t);
      if (!style) return t.text;
      // `baseStyle` первым: у кода свой моноширинный шрифт, и он должен
      // перекрывать базовый, а не наоборот.
      return (
        <Text key={`t${counter.n++}`} style={[baseStyle, style]}>
          {t.text}
        </Text>
      );
    });
    if (wrapStyle) {
      out.push(
        <Text key={`w${counter.n++}`} style={[baseStyle, wrapStyle]}>
          {nodes}
        </Text>,
      );
    } else {
      out.push(...nodes);
    }
  };

  const blocks = splitCodeFences(text);
  blocks.forEach((block, bi) => {
    if (bi > 0 && blocks[bi - 1].code) out.push('\n');

    if (block.code) {
      out.push(
        <Text key={`c${counter.n++}`} style={[baseStyle, CODE_STYLE]}>
          {block.text}
        </Text>,
      );
      return;
    }

    const lines = block.text.split('\n');
    lines.forEach((line, li) => {
      if (li > 0) out.push('\n');

      const heading = line.match(/^(#{1,6})\s+(.*)$/);
      if (heading) {
        pushTokens(heading[2], HEADING_STYLE);
        return;
      }

      const quote = line.match(/^\s*>\s?(.*)$/);
      if (quote) {
        out.push('▎ ');
        pushTokens(quote[1], QUOTE_STYLE);
        return;
      }

      const bullet = line.match(/^(\s*)[-+*]\s+(.*)$/);
      if (bullet) {
        out.push(`${bullet[1]}•  `);
        pushTokens(bullet[2]);
        return;
      }

      const ordered = line.match(/^(\s*)(\d+)[.)]\s+(.*)$/);
      if (ordered) {
        out.push(`${ordered[1]}${ordered[2]}. `);
        pushTokens(ordered[3]);
        return;
      }

      pushTokens(line);
    });
  });

  return out;
}

interface CodeBlockPart {
  code: boolean;
  text: string;
}

function splitCodeFences(text: string): CodeBlockPart[] {
  if (!text.includes('```')) return [{ code: false, text }];

  const parts: CodeBlockPart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  FENCE_RE.lastIndex = 0;
  while ((m = FENCE_RE.exec(text)) !== null) {
    if (m.index > last) parts.push({ code: false, text: text.slice(last, m.index) });
    parts.push({ code: true, text: m[1].replace(/\n$/, '') });
    last = FENCE_RE.lastIndex;
  }
  if (last < text.length) parts.push({ code: false, text: text.slice(last) });
  return parts;
}
